import ErrorIcon from '@/public/icons/error-filled.svg';
import classNames from 'classnames';
import * as React from 'react';

import styles from './Field.module.scss';

export type FieldOrientation = 'vertical' | 'horizontal' | 'responsive';

export type FieldProps = React.ComponentProps<'div'> & {
  orientation?: FieldOrientation;
  error?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'medium';
};

export const FormControl = React.forwardRef<HTMLDivElement, FieldProps>(function Field(
  { className, orientation = 'vertical', error = false, required = false, fullWidth = false, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={classNames(styles.field, className, fullWidth && 'fullWidth')}
      data-invalid={error ? 'true' : 'false'}
      data-orientation={orientation}
      data-slot="field"
      role="group"
      data-required={required ? 'true' : 'false'}
      data-full-width={fullWidth ? 'true' : 'false'}
      {...props}
    />
  );
});

FormControl.displayName = 'FormControl';

export type FieldLabelProps = React.ComponentProps<'label'> & {
  required?: boolean;
};

export const FormLabel = React.forwardRef<HTMLLabelElement, FieldLabelProps>(function FieldLabel(
  { className, required = false, children, ...props },
  ref
) {
  return (
    <label ref={ref} className={classNames(styles.fieldLabel, className)} data-slot="field-label" {...props}>
      {children}
      {required ? ' *' : ''}
    </label>
  );
});

FormLabel.displayName = 'FieldLabel';

export type FieldDescriptionProps = React.ComponentProps<'p'>;

export const FieldDescription = React.forwardRef<HTMLParagraphElement, FieldDescriptionProps>(function FieldDescription(
  { className, ...props },
  ref
) {
  return (
    <p ref={ref} className={classNames(styles.fieldDescription, className)} data-slot="field-description" {...props} />
  );
});

FieldDescription.displayName = 'FieldDescription';

export type FieldErrorProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  children?: React.ReactNode;
  errors?: Array<{ message?: string } | undefined>;
  error?: boolean;
};

export const FormHelperText = React.forwardRef<HTMLDivElement, FieldErrorProps>(function FieldError(
  { className, children, errors, error: _error, ...props },
  ref
) {
  const content = React.useMemo(() => {
    if (children) {
      return children;
    }
    if (!errors?.length) {
      return null;
    }
    const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()];
    if (uniqueErrors.length === 1) {
      return uniqueErrors[0]?.message ?? null;
    }
    return (
      <ul className={styles.fieldErrorList}>
        {uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div ref={ref} className={classNames(styles.fieldError, className)} data-slot="field-error" role="alert" {...props}>
      <div className={styles.icon}>
        <ErrorIcon />
      </div>
      {content}
    </div>
  );
});

FormHelperText.displayName = 'FieldError';
